import { useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, Swords, Trophy, User, BarChart3 } from "lucide-react";
import { isAuctionOrSeasonRoute } from "../lib/bottomNavVisibility";

const BottomNav = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dockRef = useRef(null);
  const dragIndexRef = useRef(null);
  const [dragX, setDragX] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);

  if (!user || user.role === "auction_bidder" || isAuctionOrSeasonRoute(location.pathname)) return null;

  const focusedRoutes = ["/create-match", "/scoring/", "/broadcast/", "/join/", "/toss", "/admin", "/shop-mart/admin"];
  const isTournamentDetailRoute = /^\/tournament\/[^/]+(?:\/.*)?$/.test(location.pathname);
  const isLiveAuctionRoute = /^\/tournaments\/[^/]+\/auction\/?$/.test(location.pathname);
  if (isTournamentDetailRoute || isLiveAuctionRoute || focusedRoutes.some((path) => location.pathname === path || location.pathname.startsWith(path))) {
    return null;
  }

  const tabs = [
    { path: "/dashboard", label: "Home", icon: Home },
    { path: "/matches", label: "Matches", icon: Swords },
    { path: "/stats", label: "Performance", icon: BarChart3 },
    { path: "/tournaments", label: "Tournaments", icon: Trophy },
    { path: "/profile", label: "Profile", icon: User },
  ];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + "/");
  const activeIndex = tabs.findIndex(({ path }) => isActive(path));
  const displayIndex = dragIndex ?? activeIndex;
  const ActiveIcon = displayIndex >= 0 ? tabs[displayIndex].icon : null;
  const restingLeft = activeIndex >= 0 ? `${((activeIndex + 0.5) / tabs.length) * 100}%` : "10%";

  const updateDragPosition = (clientX) => {
    const dock = dockRef.current;
    if (!dock) return;
    const bounds = dock.getBoundingClientRect();
    const cellWidth = bounds.width / tabs.length;
    const minX = cellWidth / 2;
    const maxX = bounds.width - minX;
    const nextX = Math.min(maxX, Math.max(minX, clientX - bounds.left));
    const nextIndex = Math.min(tabs.length - 1, Math.max(0, Math.round((nextX - minX) / cellWidth)));
    dragIndexRef.current = nextIndex;
    setDragX(nextX);
    setDragIndex(nextIndex);
  };

  const handlePointerDown = (event) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    updateDragPosition(event.clientX);
  };

  const handlePointerMove = (event) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
    updateDragPosition(event.clientX);
  };

  const finishDrag = (event) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    const targetIndex = dragIndexRef.current;
    if (targetIndex !== null && targetIndex !== activeIndex) {
      navigate(tabs[targetIndex].path);
    }
    dragIndexRef.current = null;
    setDragX(null);
    setDragIndex(null);
  };

  const cancelDrag = (event) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragIndexRef.current = null;
    setDragX(null);
    setDragIndex(null);
  };

  const indicatorStyle = {
    left: dragX === null ? restingLeft : `${dragX}px`,
    transition: dragX === null ? "left 460ms cubic-bezier(0.22, 1, 0.36, 1)" : "none",
  };

  return (
    <nav
      className="pointer-events-none fixed inset-x-0 bottom-0 z-50 px-3 md:hidden"
      style={{
        paddingBottom: "max(10px, var(--app-safe-bottom))",
        transform: "translateZ(0)",
        WebkitTransform: "translateZ(0)",
      }}
      aria-label="Primary navigation"
      data-testid="bottom-nav"
    >
      <div ref={dockRef} className="pointer-events-auto relative mx-auto h-[84px] w-full max-w-lg">
        <div
          className="absolute inset-x-0 bottom-0 h-[68px] rounded-lg border border-slate-700/80 bg-[#0b1019]/95 shadow-[0_18px_45px_rgba(0,0,0,0.48)] backdrop-blur-xl"
          data-bottom-nav-surface
        />

        {activeIndex >= 0 && (
          <>
            <div
              className="pointer-events-none absolute bottom-[32px] z-10 h-[66px] w-[66px] -translate-x-1/2 rounded-full bg-[#020617]"
              style={indicatorStyle}
              aria-hidden="true"
              data-bottom-nav-cutout
            />
            <div
              className="pointer-events-none absolute bottom-[37px] z-20 h-14 w-14 -translate-x-1/2 rounded-full bg-[#CCFF00]/25 blur-lg"
              style={indicatorStyle}
              aria-hidden="true"
              data-bottom-nav-glow
            />
            <button
              type="button"
              className="absolute bottom-[38px] z-40 flex h-14 w-14 -translate-x-1/2 touch-none items-center justify-center rounded-full border-[5px] border-[#0b1019] bg-[#CCFF00] text-[#020617] shadow-[0_8px_24px_rgba(204,255,0,0.28)] active:scale-95"
              style={indicatorStyle}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={finishDrag}
              onPointerCancel={cancelDrag}
              onKeyDown={(event) => {
                if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
                event.preventDefault();
                const direction = event.key === "ArrowRight" ? 1 : -1;
                const nextIndex = Math.min(tabs.length - 1, Math.max(0, activeIndex + direction));
                navigate(tabs[nextIndex].path);
              }}
              aria-label={`${tabs[displayIndex].label}. Drag or use arrow keys to switch tabs.`}
              data-bottom-nav-active
            >
              {ActiveIcon && <ActiveIcon className="h-5 w-5" strokeWidth={2.4} />}
            </button>
          </>
        )}

        <div className="absolute inset-x-0 bottom-0 z-30 flex h-[68px] items-stretch px-1">
          {tabs.map(({ path, label, icon: Icon }, index) => {
            const active = displayIndex === index;
            return (
              <Link
                key={path}
                to={path}
                aria-current={isActive(path) ? "page" : undefined}
                aria-label={label}
                data-testid={`bottom-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`relative flex min-w-0 flex-1 flex-col items-center justify-end gap-1 pb-2 text-slate-500 transition-colors duration-300 hover:text-slate-300 ${active ? "text-[#CCFF00]" : ""}`}
              >
                <Icon
                  className={`h-5 w-5 transition-all duration-300 ${active ? "scale-75 opacity-0" : "opacity-100"}`}
                  strokeWidth={1.7}
                />
                <span
                  className={`max-w-full truncate px-1 text-[10px] font-semibold leading-none transition-colors duration-300 ${active ? "text-[#CCFF00]" : "text-slate-400"}`}
                >
                  {label}
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default BottomNav;
